<template>
  <div class="challenges-panel">

    <div v-if="loading" class="text-center py-4 silver">
      <i class="fas fa-spinner fa-spin fa-2x gold"></i>
      <div class="small mt-2">Loading programs...</div>
    </div>

    <div v-else-if="challenges && challenges.length" class="challenges-scroll">
      <div
        v-for="challenge in challenges"
        :key="challenge.id"
        class="program-card"
        role="button"
        tabindex="0"
        @click="goToChallenge(challenge)"
        @keydown.enter.prevent="goToChallenge(challenge)"
      >
        <div class="program-card-header">
          <div class="program-title-wrap">
            <span class="program-icon">
              <i class="fas fa-graduation-cap"></i>
            </span>
            <span class="program-title" v-text="challenge.title"></span>
          </div>

          <span
            v-if="isFutureActive(challenge)"
            class="program-status-badge badge-upcoming"
          >
            <i class="fas fa-clock"></i>
            <span class="status-text">Upcoming</span>
          </span>
          <span
            v-else-if="typeof challenge.status !== 'undefined' && challenge.status !== null"
            class="program-status-badge"
            :class="statusBadgeClass(challenge.status)"
          >
            <i class="fas" :class="statusIcon(challenge.status)"></i>
            <span class="status-text">{{ statusText(challenge.status) }}</span>
          </span>
        </div>

        <div v-if="challenge.description" class="program-description" v-text="challenge.description"></div>

        <div class="program-card-footer">
          <div class="program-cta gold">
            <i class="fas fa-chevron-right"></i>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-muted small py-2">
      No program has been enrolled yet...
    </div>
  </div>
</template>

<script>
import axios from '@/axios';

export default {
  data() {
    return {
      challenges: [],
      loading: false,
    };
  },
  async mounted() {
    this.loading = true;
    try {
      const response = await axios.get('programs', { params: { joined: 1 } });
      this.challenges = response.data || [];
    } finally {
      this.loading = false;
    }
  },
  methods: {
    isFutureActive(challenge) {
      if (parseInt(challenge.status, 10) !== 1) return false;
      if (!challenge.start_date) return false;
      return new Date(challenge.start_date) > new Date();
    },
    goToChallenge(challenge) {
      if (!challenge || !challenge.id) return;
      this.$router.push('/programs/' + challenge.id);
    },
    statusText(status) {
      switch (parseInt(status, 10)) {
        case 1:
          return 'Active';
        case 2:
          return 'Pending';
        case 3:
          return 'Archived';
        case 0:
          return 'Closed';
        default:
          return 'Status';
      }
    },
    statusIcon(status) {
      switch (parseInt(status, 10)) {
        case 1:
          return 'fa-bolt';
        case 2:
          return 'fa-hourglass-half';
        case 3:
          return 'fa-archive';
        case 0:
          return 'fa-ban';
        default:
          return 'fa-circle';
      }
    },
    statusBadgeClass(status) {
      const s = parseInt(status, 10);
      return {
        'badge-active': s === 1,
        'badge-pending': s === 2,
        'badge-archived': s === 3,
        'badge-closed': s === 0,
      };
    },
    leaderLabel(leaders) {
      const labels = leaders
        .map((l) => l && l.label)
        .filter(Boolean);
      if (labels.length === 0) return 'Program leader';
      if (labels.length === 1) return labels[0];
      return `${labels[0]} +${labels.length - 1}`;
    },
  },
};
</script>

<style scoped>
.challenges-panel {
  width: 100%;
}

.challenges-scroll {
  padding-right: 4px;
}

.program-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.program-card:last-child {
  margin-bottom: 0;
  border-bottom: none;
}

.program-card:hover {
  transform: translateY(-1px);
  border-color: rgba(214, 182, 116, 0.55);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}

.program-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.program-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.program-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #d6b674 0%, #c5a563 100%);
  color: #fff;
  flex-shrink: 0;
}

.program-title {
  font-weight: 500;
  font-size: 0.88rem;
  color: #040404;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.program-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 0.68rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
}

.badge-active {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.16) 0%, rgba(196, 165, 90, 0.18) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.55);
}

.badge-pending {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.12) 0%, rgba(196, 165, 90, 0.14) 100%);
  color: #b8963e;
  border: 1px solid rgba(214, 182, 116, 0.35);
}

.badge-archived {
  background: linear-gradient(135deg, rgba(160, 160, 160, 0.12) 0%, rgba(120, 120, 120, 0.12) 100%);
  color: #666;
  border: 1px solid rgba(160, 160, 160, 0.35);
}

.badge-upcoming {
  background: linear-gradient(135deg, rgba(214, 182, 116, 0.12) 0%, rgba(196, 165, 90, 0.14) 100%);
  color: #b8963e;
  border: 1px dashed rgba(214, 182, 116, 0.6);
}

.badge-closed {
  background: linear-gradient(135deg, rgba(120, 120, 120, 0.12) 0%, rgba(90, 90, 90, 0.12) 100%);
  color: #666;
  border: 1px solid rgba(120, 120, 120, 0.35);
}

.program-description {
  margin-top: 8px;
  color: #666;
  font-size: 0.9rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.program-card-footer {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.program-leaders {
  color: #777;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0;
}

.program-cta {
  font-size: 0.9rem;
}

/* Ensure BootstrapVue `variant="outline-gold"` looks right inside this component. */
.btn-outline-gold {
  color: #d6b674;
  border-color: #d6b674;
  background: transparent;
}

.btn-outline-gold:hover {
  color: #fff;
  background: linear-gradient(135deg, #d6b674 0%, #c4a55a 100%);
  border-color: #d6b674;
}
</style>
